<template>
  <div class="analytics-chart-doughnut">
    <div class="message message-info" v-if="isEmpty">データがありません。</div>
    <div v-else>
      <chart-doughnut ref="chart" :chart-data="chartData" :options="options" :style="chartStyles"></chart-doughnut>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import ChartDoughnut from '../chart_doughnut'
import { DEFAULT_COLORS, resolveColors } from './analytics_chart_doughnut.utils'

type DoughnutItem = { label: string; count: number }

type DoughnutChartData = {
  labels: string[] | null
  datasets: { backgroundColor: string[]; data: number[] | null }[]
}

type LegendItem = { text: string; fillStyle: string; strokeStyle: string }

type ChartDoughnutInstance = { update: () => void }

export default defineComponent({
  components: { ChartDoughnut },
  props: {
    data: {
      type: Array as PropType<readonly DoughnutItem[]>,
      default: () => []
    },
    height: {
      type: Number,
      default: 0
    },
    chartStyles: {
      type: Object,
      default: () => ({})
    },
    isTikTok: {
      type: Boolean,
      default: false
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    colors: {
      type: Array as PropType<string[]>,
      default: () => DEFAULT_COLORS
    }
  },
  watch: {
    data() {
      this.update()
    }
  },
  computed: {
    isEmpty(): boolean {
      return this.data.every(follower => follower.count === 0)
    }
  },
  data() {
    const colors = resolveColors(this.colors)
    const chartData: DoughnutChartData = {
      labels: null,
      datasets: [
        {
          backgroundColor: colors,
          data: null
        }
      ]
    }

    return {
      options: {
        title: {
          display: false,
          text: this.isTikTok ? 'トラフィックソース' : '応募者のフォロワー数'
        },
        legend: {
          display: this.showLegend,
          hidden: true,
          position: 'right',
          labels: {
            fontSize: 18,
            padding: 8,
            boxWidth: 64,
            generateLabels: function (data: {
              tooltip: { _data: { labels: string[]; datasets: { data: number[]; backgroundColor: string[] }[] } }
            }) {
              const chartData = data.tooltip._data
              const chartDataset = chartData.datasets[0]
              const chartLabels = chartData.labels
              const legendArray: LegendItem[] = []

              const totalData = chartData.datasets[0].data.reduce((total, current) => total + current, 0)

              for (let i = 0; i < chartLabels.length; i++) {
                const eachLabel = chartLabels[i]
                const eachData = chartDataset.data[i]
                const eachPercent = !totalData ? 0 : Math.round((eachData / totalData) * Math.pow(100, 2)) / 100
                const backgroundColor = chartDataset.backgroundColor[i]

                const eachLengend = {
                  text: `${eachLabel}：${eachData.toLocaleString()}人(${eachPercent}%)`,
                  fillStyle: backgroundColor,
                  strokeStyle: 'white'
                }

                legendArray.push(eachLengend)
              }

              return legendArray
            }
          }
        },
        tooltips: {
          callbacks: {
            label: function (
              tooltipItem: { index: number },
              data: { labels: string[]; datasets: { data: number[] }[] }
            ) {
              const label = data.labels[tooltipItem.index]
              const value = data.datasets[0].data[tooltipItem.index]
              return `${label}: ${value.toLocaleString()}人`
            }
          }
        }
      },
      chartData
    }
  },
  mounted() {
    this.update()
  },
  methods: {
    update() {
      const colors = resolveColors(this.colors)
      const labels = this.data.map(d => d.label)
      const data = this.data.map(d => d.count)

      this.chartData.labels = labels
      this.chartData.datasets[0].data = data
      this.chartData.datasets[0].backgroundColor = colors
      this.options.legend.display = this.showLegend

      const chart = this.$refs.chart as ChartDoughnutInstance | undefined
      if (chart) {
        if (this.height) {
          // 高さ指定時だけ追加する設定なので、options の初期値には含めない
          ;(this.options as { maintainAspectRatio?: boolean }).maintainAspectRatio = false
          ;(this.$el.querySelector('canvas#doughnut-chart') as HTMLCanvasElement).style.height = this.height + 'px'
        }

        chart.update()
      }
    }
  }
})
</script>
